// TIN-23 Parte 2: Panel moderno de notificaciones (Offcanvas lateral).
//
// Cambios de la iteración 2 (perf + íconos):
//   - Re-introducidos los íconos raster legacy (Ingreso de Items,
//     Calibraciones vencidas, etc.) para no perder identidad visual.
//   - Renderizado virtualizado por bloques: solo se muestran las primeras
//     N notificaciones (configurable). Botón "Ver más" para cargar otro
//     bloque. Evita que el navegador se cuelgue cuando hay >100 notifs.
//   - Quitado Tabs (solo había una, eran re-renders en vano).
//   - tiempoRelativo se computa una vez por notif vía useMemo (no en cada
//     pintada).
//   - React.memo en NotificacionItem para no re-renderizar la lista si solo
//     cambia el filtro/búsqueda.

import React, { useState, useMemo, useCallback } from 'react';
import { Offcanvas, Form, InputGroup, Button, Badge, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { actualizarDatos } from '../../api/common';
import { Alertassw } from '../Alertas/Alertassw';

import './PanelNotificaciones.scss';

moment.locale('es');

const TAMANO_BLOQUE = 30;

// ── Mapeo de íconos: TODOS vectoriales (Bootstrap Icons) con colores semánticos.
// Mantiene la misma "identidad" por tipo de notificación que tenían las imágenes
// raster del navbar viejo, pero nítidos, escalables y sin imágenes rotas.

// 1) Match exacto por título (cuando el backend siempre manda el mismo string)
const ICONOS_POR_TITULO = {
  'Calibraciones vencidas': { icon: 'bi-calendar-x-fill', bg: '#dc3545' },        // rojo
  'Ingreso de Items':       { icon: 'bi-box-arrow-in-down', bg: '#22abb4' },      // teal
  'Aprobación de Ítems':    { icon: 'bi-check2-square', bg: '#198754' },          // verde
  'Finalización de Ítems':  { icon: 'bi-check-all', bg: '#0f5132' },              // verde oscuro
  'Programación OT':        { icon: 'bi-calendar-week-fill', bg: '#144291' },     // azul
  'Creación OT':            { icon: 'bi-clipboard-plus-fill', bg: '#6f42c1' },    // morado
};

// 2) Match por URL específica (legacy: url === 'Actividades' venía así)
const ICONOS_POR_URL = {
  Actividades: { icon: 'bi-tools', bg: '#fd7e14' },                                // naranja
};

// 3) Match por módulo (cuando metadata.modulo está poblado, o lo inferimos)
const ICONOS_POR_MODULO = {
  requisicion: { icon: 'bi-clipboard-check', bg: '#22abb4', label: 'Requisición' },
  ot:          { icon: 'bi-card-list', bg: '#144291', label: 'Orden de Trabajo' },
  actividad:   { icon: 'bi-tools', bg: '#fd7e14', label: 'Calibración' },
  propuesta:   { icon: 'bi-file-earmark-text-fill', bg: '#6f42c1', label: 'Propuesta' },
  cotizacion:  { icon: 'bi-currency-dollar', bg: '#198754', label: 'Cotización' },
  factura:     { icon: 'bi-receipt', bg: '#0dcaf0', label: 'Factura' },
  general:     { icon: 'bi-bell-fill', bg: '#6c757d', label: 'General' },
};

// 4) Heurísticas por palabras clave del título (para notificaciones legacy
//    que no tienen metadata.modulo y no calzan en los mapas exactos)
const HEURISTICAS_TITULO = [
  { match: /vencid/i,                  cfg: { icon: 'bi-calendar-x-fill', bg: '#dc3545' } },
  { match: /aprobad|aprobac/i,         cfg: { icon: 'bi-check2-square', bg: '#198754' } },
  { match: /rechazad|rechazo/i,        cfg: { icon: 'bi-x-octagon-fill', bg: '#dc3545' } },
  { match: /finaliz|complet/i,         cfg: { icon: 'bi-check-all', bg: '#0f5132' } },
  { match: /ingres/i,                  cfg: { icon: 'bi-box-arrow-in-down', bg: '#22abb4' } },
  { match: /program/i,                 cfg: { icon: 'bi-calendar-week-fill', bg: '#144291' } },
  { match: /crea|nueva/i,              cfg: { icon: 'bi-clipboard-plus-fill', bg: '#6f42c1' } },
  { match: /requisici/i,               cfg: { icon: 'bi-clipboard-check', bg: '#22abb4' } },
  { match: /cotizac/i,                 cfg: { icon: 'bi-currency-dollar', bg: '#198754' } },
  { match: /factura/i,                 cfg: { icon: 'bi-receipt', bg: '#0dcaf0' } },
  { match: /propuest/i,                cfg: { icon: 'bi-file-earmark-text-fill', bg: '#6f42c1' } },
  { match: /devoluc/i,                 cfg: { icon: 'bi-arrow-counterclockwise', bg: '#dc3545' } },
  { match: /certificad/i,              cfg: { icon: 'bi-patch-check-fill', bg: '#198754' } },
  { match: /ganador/i,                 cfg: { icon: 'bi-trophy-fill', bg: '#ffc107' } },
  { match: /orden de compra|^oc /i,    cfg: { icon: 'bi-file-earmark-ruled-fill', bg: '#144291' } },
];

const inferirModulo = (notif) => {
  if (notif?.metadata?.modulo) return notif.metadata.modulo;
  const url = (notif?.url || '').toLowerCase();
  if (url.startsWith('/requisiciones') || url.includes('requisicion')) return 'requisicion';
  if (url.startsWith('/seguimiento') || url.includes('/ot') || url.includes('numeroorden')) return 'ot';
  if (url === 'actividades' || url.includes('actividad')) return 'actividad';
  if (url.includes('propuesta')) return 'propuesta';
  if (url.includes('cotizacion')) return 'cotizacion';
  if (url.includes('factura')) return 'factura';
  return 'general';
};

// Decide qué ícono mostrar. Orden de prioridad:
// 1) Match exacto por título (mantiene identidad visual de las legacy).
// 2) Match por URL específica.
// 3) Match por módulo (metadata o inferencia).
// 4) Heurística por palabras clave del título.
// 5) Fallback: bell gris.
const resolverIcono = (notif) => {
  const titulo = notif?.titulo || '';
  const url = notif?.url || '';

  if (ICONOS_POR_TITULO[titulo]) return ICONOS_POR_TITULO[titulo];
  if (ICONOS_POR_URL[url]) return ICONOS_POR_URL[url];

  const mod = inferirModulo(notif);
  if (mod !== 'general' && ICONOS_POR_MODULO[mod]) return ICONOS_POR_MODULO[mod];

  for (const h of HEURISTICAS_TITULO) {
    if (h.match.test(titulo)) return h.cfg;
  }

  return ICONOS_POR_MODULO.general;
};

const renderIcono = (notif) => {
  const cfg = resolverIcono(notif);
  return (
    <div className="panel-notif-icono" style={{ backgroundColor: cfg.bg }}>
      <i className={`bi ${cfg.icon}`}></i>
    </div>
  );
};

// Agrupador por fecha (Hoy / Ayer / Esta semana / Más antiguas)
const agruparPorFecha = (notifs) => {
  const grupos = { hoy: [], ayer: [], semana: [], antiguas: [] };
  const inicioHoy = moment().startOf('day');
  const inicioAyer = moment().subtract(1, 'day').startOf('day');
  const haceSieteDias = moment().subtract(7, 'day');
  for (const n of notifs) {
    const f = moment(n.createdAt);
    if (f.isSameOrAfter(inicioHoy)) grupos.hoy.push(n);
    else if (f.isSameOrAfter(inicioAyer)) grupos.ayer.push(n);
    else if (f.isSameOrAfter(haceSieteDias)) grupos.semana.push(n);
    else grupos.antiguas.push(n);
  }
  return grupos;
};

const ETIQUETAS_GRUPO = {
  hoy: 'Hoy',
  ayer: 'Ayer',
  semana: 'Esta semana',
  antiguas: 'Más antiguas',
};

// Item memoizado para que no se re-renderice si solo cambia el filtro global
const NotificacionItem = React.memo(function NotificacionItem({ notif, onClick, onCerrar, tiempoRelativo, moduloLabel }) {
  return (
    <div
      className="panel-notif-item"
      onClick={() => onClick(notif)}
      role="button"
      tabIndex={0}
    >
      {renderIcono(notif)}
      <div className="panel-notif-body">
        <div className="panel-notif-titulo">{notif.titulo}</div>
        {notif.mensage && <div className="panel-notif-mensaje">{notif.mensage}</div>}
        <div className="panel-notif-meta">
          <Badge bg="light" text="dark" className="panel-notif-modulo">
            {moduloLabel}
          </Badge>
          <small className="panel-notif-tiempo">{tiempoRelativo}</small>
        </div>
      </div>
      <button
        className="panel-notif-cerrar"
        title="Marcar como leída"
        onClick={(e) => {
          e.stopPropagation();
          onCerrar(notif);
        }}
      >
        <i className="bi bi-x"></i>
      </button>
    </div>
  );
});

const PanelNotificaciones = ({
  show,
  onHide,
  notificaciones,
  userData,
  onRefresh,
  silenciado,
  onToggleSilenciar,
}) => {
  const navigate = useNavigate();
  const [filtroModulo, setFiltroModulo] = useState('todos');
  const [busqueda, setBusqueda] = useState('');
  const [bloquesVisibles, setBloquesVisibles] = useState(1); // se incrementa con "Ver más"
  const [procesando, setProcesando] = useState(false);

  // Defensa: notificaciones siempre array
  const dataActiva = useMemo(() => (Array.isArray(notificaciones) ? notificaciones : []), [notificaciones]);

  // Pre-calculo módulo + tiempo relativo + módulo label por cada noti (1 vez)
  // + ORDEN DESCENDENTE por fecha de creación → garantiza que las 30 primeras
  // visibles sean siempre las más recientes.
  const dataEnriquecida = useMemo(() => {
    const mapeadas = dataActiva.map((n) => {
      const mod = inferirModulo(n);
      const meta = ICONOS_POR_MODULO[mod] || ICONOS_POR_MODULO.general;
      return {
        ...n,
        _modulo: mod,
        _moduloLabel: meta.label,
        _tiempoRelativo: n.createdAt ? moment(n.createdAt).fromNow() : '',
        _ts: n.createdAt ? new Date(n.createdAt).getTime() : 0,
      };
    });
    // Orden descendente: más recientes primero
    mapeadas.sort((a, b) => b._ts - a._ts);
    return mapeadas;
  }, [dataActiva]);

  // Filtros aplicables (el orden se preserva: ya está descendente)
  const filtradas = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return dataEnriquecida.filter((n) => {
      if (filtroModulo !== 'todos' && n._modulo !== filtroModulo) return false;
      if (q) {
        const t = (n.titulo || '').toLowerCase();
        const m = (n.mensage || '').toLowerCase();
        if (!t.includes(q) && !m.includes(q)) return false;
      }
      return true;
    });
  }, [dataEnriquecida, filtroModulo, busqueda]);

  // Slice por bloques. Como `filtradas` ya está ordenada desc, las primeras
  // 30 son las más recientes.
  const limite = bloquesVisibles * TAMANO_BLOQUE;
  const truncadas = useMemo(() => filtradas.slice(0, limite), [filtradas, limite]);
  const hayMas = filtradas.length > limite;

  // Agrupación por fecha sobre las truncadas (ya vienen ordenadas)
  const grupos = useMemo(() => agruparPorFecha(truncadas), [truncadas]);

  const modulosPresentes = useMemo(() => {
    const set = new Set();
    for (const n of dataEnriquecida) set.add(n._modulo);
    return Array.from(set);
  }, [dataEnriquecida]);

  // ── Acciones ─────────────────────────────────────────────────────────────

  const marcarLeida = useCallback(
    async (notif, navegar = true) => {
      try {
        await actualizarDatos(
          'Notificacion',
          { $addToSet: { leidaPor: { usuarioId: userData._id, fechaLeida: new Date() } } },
          notif._id
        );
        onRefresh?.();
        if (navegar && notif.url) navigate(notif.url);
        if (navegar) onHide?.();
      } catch (error) {
        console.error('[PanelNotif] error marcando leída:', error);
        Alertassw('toast', 'No se pudo marcar como leída', '', 'warning');
      }
    },
    [userData?._id, onRefresh, navigate, onHide]
  );

  const marcarTodasLeidas = useCallback(async () => {
    if (filtradas.length === 0) return;
    setProcesando(true);
    try {
      // En vez de Promise.allSettled — uno por uno para no abrir 500 conexiones
      for (const notif of filtradas) {
        try {
          await actualizarDatos(
            'Notificacion',
            { $addToSet: { leidaPor: { usuarioId: userData._id, fechaLeida: new Date() } } },
            notif._id
          );
        } catch {}
      }
      Alertassw('toast', `${filtradas.length} marcadas como leídas`, '', 'success');
      onRefresh?.();
    } finally {
      setProcesando(false);
    }
  }, [filtradas, userData?._id, onRefresh]);

  // Reseteo del bloque mostrado cuando cambian los filtros
  React.useEffect(() => {
    setBloquesVisibles(1);
  }, [filtroModulo, busqueda]);

  // Manejador de "cerrar" individual memoizado
  const handleCerrar = useCallback((notif) => marcarLeida(notif, false), [marcarLeida]);
  const handleClickItem = useCallback((notif) => marcarLeida(notif, true), [marcarLeida]);

  // ── Render ───────────────────────────────────────────────────────────────

  const renderGrupos = () => {
    const claves = ['hoy', 'ayer', 'semana', 'antiguas'];
    const tieneAlgo = claves.some((k) => grupos[k].length > 0);
    if (!tieneAlgo) {
      return (
        <Alert variant="light" className="text-center my-4 panel-notif-vacio">
          <i className="bi bi-inbox" style={{ fontSize: 32, color: '#aaa' }}></i>
          <p className="mb-0 mt-2 text-muted">No hay notificaciones</p>
        </Alert>
      );
    }
    return claves.map((k) =>
      grupos[k].length === 0 ? null : (
        <div key={k} className="panel-notif-grupo">
          <div className="panel-notif-grupo-titulo">{ETIQUETAS_GRUPO[k]}</div>
          {grupos[k].map((notif) => (
            <NotificacionItem
              key={notif._id}
              notif={notif}
              onClick={handleClickItem}
              onCerrar={handleCerrar}
              tiempoRelativo={notif._tiempoRelativo}
              moduloLabel={notif._moduloLabel}
            />
          ))}
        </div>
      )
    );
  };

  return (
    <Offcanvas show={show} onHide={onHide} placement="end" className="panel-notif-offcanvas">
      <Offcanvas.Header className="panel-notif-header">
        <Offcanvas.Title>
          <i className="bi bi-bell-fill me-2"></i>
          Notificaciones
          {dataActiva.length > 0 && (
            <Badge bg="danger" className="ms-2">
              {dataActiva.length > 99 ? '99+' : dataActiva.length}
            </Badge>
          )}
        </Offcanvas.Title>
        <div className="d-flex gap-2 align-items-center">
          <button
            className="panel-notif-iconbtn"
            onClick={onToggleSilenciar}
            title={silenciado ? 'Activar sonido' : 'Silenciar'}
          >
            <i className={`bi ${silenciado ? 'bi-bell-slash' : 'bi-volume-up'}`}></i>
          </button>
          <button className="panel-notif-iconbtn" onClick={onHide} title="Cerrar">
            <i className="bi bi-x-lg"></i>
          </button>
        </div>
      </Offcanvas.Header>

      <div className="panel-notif-toolbar">
        <InputGroup size="sm" className="mb-2">
          <InputGroup.Text><i className="bi bi-search"></i></InputGroup.Text>
          <Form.Control
            type="text"
            placeholder="Buscar por título o mensaje..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
          />
          {busqueda && (
            <Button variant="outline-secondary" onClick={() => setBusqueda('')}>
              <i className="bi bi-x"></i>
            </Button>
          )}
        </InputGroup>

        <div className="d-flex gap-2 align-items-center justify-content-between flex-wrap">
          <Form.Select
            size="sm"
            value={filtroModulo}
            onChange={(e) => setFiltroModulo(e.target.value)}
            style={{ maxWidth: 200 }}
          >
            <option value="todos">Todos los módulos</option>
            {modulosPresentes.map((m) => (
              <option key={m} value={m}>{ICONOS_POR_MODULO[m]?.label || m}</option>
            ))}
          </Form.Select>
          <Button
            variant="link"
            size="sm"
            onClick={marcarTodasLeidas}
            disabled={procesando || filtradas.length === 0}
            className="panel-notif-marcar-todas"
          >
            <i className="bi bi-check-all me-1"></i>
            Marcar todas como leídas
          </Button>
        </div>
      </div>

      <Offcanvas.Body className="panel-notif-body-scroll">
        {renderGrupos()}

        {hayMas && (
          <div className="text-center py-3">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setBloquesVisibles((b) => b + 1)}
            >
              <i className="bi bi-arrow-down-circle me-1"></i>
              Ver {Math.min(TAMANO_BLOQUE, filtradas.length - limite)} más
            </Button>
            <div>
              <small className="text-muted">Mostrando {limite} de {filtradas.length}</small>
            </div>
          </div>
        )}
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default PanelNotificaciones;
